import React, { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Download } from 'lucide-react'
import { DataTable } from '@workspace/ui/components/DataTable'
import { Button } from '@workspace/ui/components/Button'
import { FilterDropdown } from '@/shared/components/FilterDropdown'
import TableDataFooter from '@/shared/components/TableDataFooter'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { BsDateRangePicker, BsDateRangePickerValue } from '@workspace/ui/components/DatePicker'
import { type ActionType, type SystemLog } from '@/shared/api/logs'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import { LogDetailDialog } from '@/shared/components/logs/LogDetailDialog'
import { useLogsColumns } from '@/shared/components/logs/useLogsColumns'
import { useDateRangeQuickOptions } from '@/shared/hooks/useDateRangeQuickOptions'
import { mapAuditLogToSystemLog } from '@/shared/utils/mapAuditLogToSystemLog'
import type { AuditLogAction } from '@workspace/lib/api'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'
import type { DataTableSorting } from '@workspace/ui/components/DataTable'

const searchSchema = z.object({
    q: z.string().optional(),
    page: z.number().catch(1).optional(),
    pageSize: z.number().catch(10).optional(),
    regions: z.string().optional(),
    areas: z.string().optional(),
    departments: z.string().optional(),
    actions: z.string().optional(),
    objects: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.string().optional(),
    fromDate: z.string().optional(),
    toDate: z.string().optional(),
})

export const Route = createFileRoute('/logs/')({
    validateSearch: searchSchema,
    component: SystemLogsPage,
    beforeLoad: requirePermissionBeforeLoad('view', 'system'),
})

function SystemLogsPage() {
    const { t } = useTranslation()
    const searchParams = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })

    const updateSearch = (newSearch: Record<string, any>) => {
        navigate({ search: (prev: any) => ({ ...prev, ...newSearch }) })
    }

    const appliedSearchName = searchParams.q || ''
    const currentPage = searchParams.page || 1
    const pageSize = searchParams.pageSize || 10
    const regionValues = searchParams.regions ? searchParams.regions.split(',') : []
    const areaValues = searchParams.areas ? searchParams.areas.split(',') : []
    const departmentValues = searchParams.departments ? searchParams.departments.split(',') : []
    const actionValues = searchParams.actions ? searchParams.actions.split(',') : []
    const objectValues = searchParams.objects ? searchParams.objects.split(',') : []

    const sorting = searchParams.sortBy
        ? { sortBy: searchParams.sortBy, sortDirection: searchParams.sortDirection as 'asc' | 'desc' }
        : { sortBy: 'timestamp', sortDirection: 'desc' }

    const dateRange = {
        start: searchParams.fromDate || dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
        end: searchParams.toDate || dayjs().format('YYYY-MM-DD'),
    }

    const setAppliedSearchName = (q: string) => updateSearch({ q, page: 1 })
    const setCurrentPage = (page: number) => updateSearch({ page })
    const setPageSize = (pageSize: number) => updateSearch({ pageSize, page: 1 })
    const setRegionValues = (regions: string[]) =>
        updateSearch({ regions: regions.length > 0 ? regions.join(',') : undefined, page: 1 })
    const setAreaValues = (areas: string[]) =>
        updateSearch({ areas: areas.length > 0 ? areas.join(',') : undefined, page: 1 })
    const setDepartmentValues = (deps: string[]) =>
        updateSearch({ departments: deps.length > 0 ? deps.join(',') : undefined, page: 1 })
    const setActionValues = (acts: string[]) =>
        updateSearch({ actions: acts.length > 0 ? acts.join(',') : undefined, page: 1 })
    const setObjectValues = (objs: string[]) =>
        updateSearch({ objects: objs.length > 0 ? objs.join(',') : undefined, page: 1 })
    const setSorting = (sort: DataTableSorting | null) => {
        if (!sort) {
            updateSearch({ sortBy: undefined, sortDirection: undefined, page: 1 })
        } else {
            updateSearch({ sortBy: sort.sortBy, sortDirection: sort.sortDirection, page: 1 })
        }
    }
    const setDateRange = (range: BsDateRangePickerValue | undefined) => {
        updateSearch({
            fromDate: range?.start || undefined,
            toDate: range?.end || undefined,
            page: 1,
        })
    }

    const [searchName, setSearchName] = useState(appliedSearchName)
    const [selectedLog, setSelectedLog] = useState<SystemLog | null>(null)
    const [isDetailOpen, setIsDetailOpen] = useState(false)

    const quickOptions = useDateRangeQuickOptions()

    const { data: regionsData } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    const { data: areasData } = useQuery({
        queryKey: ['areas'],
        queryFn: () =>
            api.area.list({
                page: 1,
                take: 100,
            }),
    })

    const { data: departmentsData } = useQuery({
        queryKey: ['departments'],
        queryFn: () => api.department.list({ page: 1, take: 100 }),
    })

    const { data: usersData } = useQuery({
        queryKey: ['users', appliedSearchName],
        enabled: !!appliedSearchName,
        queryFn: () => api.user.listAll({ page: 1, take: 50, search: appliedSearchName }),
    })

    const regionOptions = useMemo(
        () => regionsData?.data.map(region => ({ id: region.id, name: region.name })) ?? [],
        [regionsData],
    )

    const areaOptions = useMemo(() => {
        const allAreas = areasData?.data || []
        if (regionValues.length === 0) return allAreas.map((area: any) => ({ id: area.id, name: area.name }))
        return allAreas
            .filter((area: any) => regionValues.includes(area.region?.id))
            .map((area: any) => ({ id: area.id, name: area.name }))
    }, [areasData, regionValues])

    // Auto-prune areaValues when regionValues change
    useEffect(() => {
        if (!areasData?.data || areaValues.length === 0) return

        if (regionValues.length === 0) {
            updateSearch({ areas: undefined })
            return
        }

        const validAreaIds = new Set(
            areasData.data.filter((a: any) => regionValues.includes(a.region?.id)).map((a: any) => a.id),
        )

        const newAreaValues = areaValues.filter(id => validAreaIds.has(id))

        if (newAreaValues.length !== areaValues.length) {
            updateSearch({
                areas: newAreaValues.length > 0 ? newAreaValues.join(',') : undefined,
                page: 1,
            })
        }
    }, [regionValues, areaValues, areasData])

    const departmentOptions = useMemo(
        () => departmentsData?.data.map(dept => ({ id: dept.id, name: dept.name })) ?? [],
        [departmentsData],
    )

    const actionOptions = useMemo(
        () =>
            [
                { id: 'login', name: t('logs.actions.login') },
                { id: 'logout', name: t('logs.actions.logout') },
                { id: 'view', name: t('logs.actions.view') },
                { id: 'add', name: t('logs.actions.add') },
                { id: 'edit', name: t('logs.actions.edit') },
                { id: 'delete', name: t('logs.actions.delete') },
            ] satisfies { id: AuditLogAction; name: string }[],
        [t],
    )

    const objectOptions = useMemo(
        () => [
            { id: 'system', name: t('logs.objects.system') },
            { id: 'application', name: t('logs.objects.application') },
            { id: 'users', name: t('logs.objects.users') },
            { id: 'category', name: t('logs.objects.category') },
            { id: 'project', name: t('logs.objects.project') },
            { id: 'profile', name: t('logs.objects.profile') },
            { id: 'approval', name: t('logs.objects.approval') },
            { id: 'directive', name: t('logs.objects.directive') },
            { id: 'role', name: t('logs.objects.role') },
            { id: 'template', name: t('logs.objects.template') },
            { id: 'document', name: t('logs.objects.document') },
        ],
        [t],
    )

    const auditLogParams = useMemo(() => {
        const params: {
            page?: number
            take?: number
            order?: 'ASC' | 'DESC'
            orderBy?: string
            q?: string
            action?: string[]
            target?: string[]
            departmentIds?: string[]
            areaIds?: string[]
            regionIds?: string[]
            from?: string
            to?: string
        } = {
            page: currentPage,
            take: pageSize,
        }

        // Add sorting params if sorting is set
        if (sorting) {
            params.order = sorting.sortDirection.toUpperCase() as 'ASC' | 'DESC'
            // Map column id to API field name
            const orderByMap: Record<string, string> = {
                timestamp: 'createdAt',
                fullName: 'name',
                identification: 'identification',
                department: 'department',
                action: 'action',
                object: 'target',
            }
            params.orderBy = orderByMap[sorting.sortBy] || 'createdAt'
        }

        // Search filter - support broader backend search fields
        if (appliedSearchName) {
            // Keep legacy 'q' param
            params.q = appliedSearchName
            // Also send 'search' which some backend implementations use for full-text search
            params.search = appliedSearchName
            // Add explicit hint for searching user full name (backend may index this)
            ;(params as any).userFullName = appliedSearchName

            // If Users API returned matches, prefer sending userId/userEmail for accurate lookup
            if (usersData?.data && usersData.data.length > 0) {
                const normalize = (s?: string) =>
                    (s ?? '')
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .toLowerCase()
                        .trim() ?? ''
                const normalizedQuery = normalize(appliedSearchName)
                // Match strategies: exact -> contains -> token match -> single result fallback
                const exactMatch = usersData.data.find(u => normalize(u.fullName) === normalizedQuery)
                const containsMatch = usersData.data.find(u => normalize(u.fullName).includes(normalizedQuery))
                const tokenMatch = usersData.data.find(u => {
                    const tokens = normalize(u.fullName).split(/\s+/)
                    return tokens.some(tok => normalizedQuery.includes(tok) || tok.includes(normalizedQuery))
                })
                const bestMatch =
                    exactMatch ||
                    containsMatch ||
                    tokenMatch ||
                    (usersData.data.length === 1 ? usersData.data[0] : undefined)
                if (bestMatch) {
                    ;(params as any).userId = bestMatch.id
                    ;(params as any).userEmail = bestMatch.email
                }
            } else if (appliedSearchName.includes('@')) {
                // If the input looks like an email but Users API didn't return results, still hint backend
                ;(params as any).userEmail = appliedSearchName
            }
        }

        // Action filter - Hỗ trợ nhiều giá trị
        if (actionValues.length > 0) {
            params.action = actionValues
        }

        // Target/Object filter - Hỗ trợ nhiều giá trị
        if (objectValues.length > 0) {
            params.target = objectValues
        }

        // Location filters - GỬI DƯỚI DẠNG ARRAY
        if (regionValues.length > 0) {
            params.regionIds = regionValues
        }

        if (areaValues.length > 0) {
            params.areaIds = areaValues
        }

        if (departmentValues.length > 0) {
            params.departmentIds = departmentValues
        }

        // Date range filters
        if (dateRange?.start) {
            params.from = dayjs(dateRange.start).startOf('day').toISOString()
        }

        if (dateRange?.end) {
            params.to = dayjs(dateRange.end).endOf('day').toISOString()
        }
        return params
    }, [
        currentPage,
        pageSize,
        sorting,
        appliedSearchName,
        actionValues,
        objectValues,
        regionValues,
        areaValues,
        departmentValues,
        dateRange,
        usersData,
    ])

    const {
        data: auditLogsData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['audit-logs', auditLogParams],
        queryFn: () => api.auditLog.list(auditLogParams),
    })

    const systemLogs = useMemo(() => {
        if (!auditLogsData?.data || auditLogsData.data.length === 0) return []
        return auditLogsData.data.map(log => mapAuditLogToSystemLog(log))
    }, [auditLogsData])

    const totalResults = auditLogsData?.meta.itemCount || 0
    const totalPages = auditLogsData?.meta.pageCount || 0

    const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            setAppliedSearchName(searchName)
            setCurrentPage(1)
        }
    }

    const handleSearchChange = (value: string) => {
        setSearchName(value)
        // If cleared (empty), immediately apply the search to reload data
        if (value === '') {
            setAppliedSearchName('')
            setCurrentPage(1)
        }
    }

    const handleRegionChange = (values: string[]) => {
        if (values.length === 0) {
            setAreaValues([])
        }
        setRegionValues(values)
        setCurrentPage(1)
    }

    const handleAreaChange = (values: string[]) => {
        setAreaValues(values)
        setCurrentPage(1)
    }

    const handleDepartmentChange = (values: string[]) => {
        setDepartmentValues(values)
        setCurrentPage(1)
    }

    const handleActionChange = (values: string[]) => {
        setActionValues(values)
        setCurrentPage(1)
    }

    const handleObjectChange = (values: string[]) => {
        setObjectValues(values)
        setCurrentPage(1)
    }

    const handleDateRangeChange = (value: BsDateRangePickerValue | undefined) => {
        setDateRange(value)
        setCurrentPage(1)
    }

    const handleExportCsv = async () => {
        const { page, take, ...exportParams } = auditLogParams ?? {}
        const blob = await api.auditLog.exportCsv(exportParams)
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `audit-logs-${dayjs().format('YYYY-MM-DD')}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    const openDetail = (log: SystemLog) => {
        setSelectedLog(log)
        setIsDetailOpen(true)
    }

    const handleSortingChange = (newSorting: DataTableSorting | null) => {
        setSorting(newSorting)
        setCurrentPage(1)
    }

    const handlePageSizeChange = (size?: number) => {
        const newSize = size ?? 10
        setPageSize(newSize)
        setCurrentPage(1)
    }

    const columns = useLogsColumns(openDetail)

    return (
        <>
            <div className="flex flex-col gap-4 max-lg:gap-2 h-[calc(100vh-200px)] min-h-[500px] overflow-hidden">
                <h1 className="text-2xl font-bold mb-4 shrink-0 max-lg:mb-2">{t('logs.title')}</h1>
                <div className="flex flex-col gap-4 max-lg:gap-2 min-h-0 flex-1">
                    <div className="flex gap-4 flex-wrap items-center justify-between shrink-0">
                        <div className="flex gap-4 flex-wrap items-center max-lg:w-full">
                            <BsSearchField
                                autoTrim
                                className="h-10 text-sm bg-background rounded-lg w-56 max-lg:w-full"
                                placeholder={t('logs.searchPlaceholder')}
                                containerClassName={'max-lg:w-full'}
                                value={searchName}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeydown}
                            />
                            <FilterDropdown
                                placeholder={t('logs.filters.region.label')}
                                inputPlaceholder={t('logs.filters.region.input')}
                                options={regionOptions}
                                selectedValues={regionValues}
                                onChange={handleRegionChange}
                                containerClassName="max-lg:flex-1"
                                className="max-lg:w-full"
                            />
                            <FilterDropdown
                                placeholder={t('logs.filters.area.label')}
                                inputPlaceholder={t('logs.filters.area.input')}
                                options={areaOptions}
                                selectedValues={areaValues}
                                onChange={handleAreaChange}
                                containerClassName="max-lg:flex-1"
                                className="max-lg:w-full"
                                isDisabled={regionValues.length === 0}
                                disabledTooltip={t('common.filters.requireRegionFirst')}
                            />
                            <FilterDropdown
                                placeholder={t('logs.filters.department.label')}
                                inputPlaceholder={t('logs.filters.department.input')}
                                options={departmentOptions}
                                selectedValues={departmentValues}
                                onChange={handleDepartmentChange}
                                containerClassName="max-lg:flex-1"
                                className="max-lg:w-full"
                            />
                            <FilterDropdown
                                placeholder={t('logs.filters.action.label')}
                                inputPlaceholder={t('logs.filters.action.input')}
                                options={actionOptions}
                                selectedValues={actionValues}
                                onChange={handleActionChange}
                                containerClassName="max-lg:flex-1"
                                className="max-lg:w-full"
                            />
                            <FilterDropdown
                                placeholder={t('logs.filters.object.label')}
                                inputPlaceholder={t('logs.filters.object.input')}
                                options={objectOptions}
                                selectedValues={objectValues}
                                onChange={handleObjectChange}
                                containerClassName="max-lg:flex-1"
                                className="max-lg:w-full"
                            />
                        </div>
                        <div className="flex gap-4 items-center max-lg:w-full">
                            <BsDateRangePicker
                                value={dateRange}
                                onChange={handleDateRangeChange}
                                visibleMonths={1}
                                quickOptions={quickOptions}
                                className="w-auto min-w-[200px] [&>div]:h-10 [&>div]:rounded-lg [&_input]:text-sm [&_input]:text-foreground [&_span]:text-sm [&_span]:text-foreground max-lg:w-full"
                            />
                            <Button
                                variant="outline"
                                className="h-10 bg-transparent hover:bg-transparent"
                                onClick={handleExportCsv}
                                isDisabled={isLoading}
                            >
                                <Download className="size-4 mr-2" />
                                {t('logs.exportCsv')}
                            </Button>
                        </div>
                    </div>
                    <DataTable
                        data={systemLogs}
                        columns={columns}
                        enableRowSelection={false}
                        enableSorting={true}
                        sorting={sorting}
                        setSorting={handleSortingChange}
                        isLoading={isLoading}
                        emptyString={t('logs.empty')}
                        containerClassName="flex-1 overflow-y-auto min-h-0"
                    />
                    <div className="shrink-0">
                        <TableDataFooter
                            selectedCount={0}
                            onDelete={() => {}}
                            deleteDisabled={true}
                            totalCount={totalResults}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </div>
                </div>
            </div>

            <LogDetailDialog open={isDetailOpen} onOpenChange={setIsDetailOpen} log={selectedLog} />
        </>
    )
}
